/**
 * Session state.
 * The access token lives in memory (never localStorage) so an XSS payload
 * cannot read it; the long-lived refresh token stays in an httpOnly cookie.
 */
import { create } from 'zustand';
import { http, setAccessToken, setOnUnauthorized } from '../api/client';
import type { AuthUser } from '../types';

interface AuthState {
  user: AuthUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  signIn: (identifier: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
  /** Re-read /auth/me with the current token (keeps the session alive). */
  refreshUser: () => Promise<void>;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
}

export const useAuth = create<AuthState>((set, get) => {
  // A 401 anywhere in the app drops us back to the login screen.
  setOnUnauthorized(() => {
    setAccessToken(null);
    set({ user: null, status: 'anonymous' });
  });

  return {
    user: null,
    status: 'loading',

    async signIn(identifier, password) {
      const data = await http.post<{ accessToken: string; user: AuthUser }>('/auth/login', { identifier, password });
      setAccessToken(data.accessToken);
      set({ user: data.user, status: 'authenticated' });
      return data.user;
    },

    async signOut() {
      try { await http.post('/auth/logout'); } catch { /* best effort */ }
      setAccessToken(null);
      set({ user: null, status: 'anonymous' });
    },

    /** On a full page load, try the refresh cookie before showing the login. */
    async restore() {
      try {
        const data = await http.post<{ accessToken: string; user: AuthUser }>('/auth/refresh');
        setAccessToken(data.accessToken);
        set({ user: data.user, status: 'authenticated' });
      } catch {
        setAccessToken(null);
        set({ user: null, status: 'anonymous' });
      }
    },

    async refreshUser() {
      try {
        const data = await http.get<{ user: AuthUser }>('/auth/me');
        set({ user: data.user, status: 'authenticated' });
      } catch {
        // Leave the session as-is; a 401 will be handled by the client interceptor.
      }
    },

    can: (permission) => get().user?.permissions.includes(permission) ?? false,
    canAny: (...permissions) => {
      const held = get().user?.permissions ?? [];
      return permissions.some((p) => held.includes(p));
    },
  };
});
